import {
  createContext,
  type ReactElement,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { readLocalStorage, writeLocalStorage } from '../localStorageGuard.js';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'room-mates.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

function isTheme(value: string | null): value is Theme {
  return value === 'light' || value === 'dark';
}

/** Guarded because jsdom (the test environment) has no real `matchMedia`. */
function safeMatchMedia(query: string): MediaQueryList | null {
  try {
    return globalThis.matchMedia?.(query) ?? null;
  } catch {
    return null;
  }
}

function systemPrefersDark(): boolean {
  return safeMatchMedia(DARK_QUERY)?.matches ?? false;
}

export interface ThemeContextValue {
  /** What is actually applied right now — an explicit choice, or the system's. */
  readonly theme: Theme;
  /** Whether `theme` came from an explicit choice or is still following the system. */
  readonly isExplicit: boolean;
  toggleTheme(): void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Light and dark, defaulting to the system's own preference.
 *
 * Nothing is written to `<html>` until the reader picks a side: the plain CSS
 * `prefers-color-scheme` rule already does the right thing with zero JavaScript,
 * so only an explicit choice — which has to win over a system change the reader
 * never asked to follow — earns a `data-theme` override.
 */
export function ThemeProvider({ children }: { children: ReactNode }): ReactElement {
  const [explicitTheme, setExplicitTheme] = useState<Theme | null>(() => {
    const stored = readLocalStorage(STORAGE_KEY);
    return isTheme(stored) ? stored : null;
  });
  const [prefersDark, setPrefersDark] = useState(systemPrefersDark);

  useEffect(() => {
    const media = safeMatchMedia(DARK_QUERY);
    if (media === null) {
      return;
    }
    const onChange = (event: MediaQueryListEvent): void => setPrefersDark(event.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const theme: Theme = explicitTheme ?? (prefersDark ? 'dark' : 'light');

  useEffect(() => {
    if (explicitTheme === null) {
      delete document.documentElement.dataset.theme;
    } else {
      document.documentElement.dataset.theme = explicitTheme;
    }
  }, [explicitTheme]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      isExplicit: explicitTheme !== null,
      toggleTheme: () => {
        const next: Theme = theme === 'dark' ? 'light' : 'dark';
        writeLocalStorage(STORAGE_KEY, next);
        setExplicitTheme(next);
      },
    }),
    [theme, explicitTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
